<template>
  <div class="page">
    <div class="container">
      <div class="page-header">
        <h1>PLLG Tracker</h1>
        <Button class="add-btn" label="Add Referral" icon="pi pi-plus" @click="openAddModal" v-if="proxy.$authStore.user?.role?.includes('pllg-staff')" />
      </div>

      <div class="view-controls">
        <div class="view-buttons">
          <Select v-model="filterCongress" :options="congressFilterOptions" optionLabel="label" optionValue="value" placeholder="All Congress" showClear class="filter-select" @change="applyFilters" />
          <Button :label="'Table'" size="small" :severity="viewMode === 'table' ? undefined : 'secondary'" icon="pi pi-list" @click="viewMode = 'table'" />
          <Button :label="'Two Weeks'" size="small" :severity="viewMode === 'twoweeks' ? undefined : 'secondary'" icon="pi pi-calendar" @click="viewMode = 'twoweeks'" />
          <Button :label="'Month'" size="small" :severity="viewMode === 'month' ? undefined : 'secondary'" icon="pi pi-calendar-plus" @click="viewMode = 'month'" />
          <Button :label="'Metrics'" size="small" :severity="viewMode === 'metrics' ? undefined : 'secondary'" icon="pi pi-chart-bar" @click="viewMode = 'metrics'" />
        </div>
      </div>

      <!-- Table View -->
      <DataTable 
        v-if="viewMode === 'table'"
        :value="filteredReferrals" 
        :paginator="true" 
        :rows="10"
        :loading="loading"
        stripedRows
        showGridlines
        v-model:filters="filters"
        filterDisplay="menu"
        scrollable
        scrollHeight="600px"
      >
        <template #header>
          <div class="table-filters">
            <div class="filters-grid">
              <IconField iconPosition="left" class="search-field">
                <InputIcon class="pi pi-search" />
                <InputText v-model="filters['global'].value" placeholder="Search all columns..." />
              </IconField>
              <Select v-model="filterRequestingParty" :options="requestingPartyOptions" optionLabel="label" optionValue="value" placeholder="Requesting Party" showClear class="filter-item" @change="applyFilters" />
              <Select v-model="filterCommittee" :options="committeeOptions" optionLabel="label" optionValue="value" placeholder="Committee" showClear class="filter-item" filter @change="applyFilters" />
              <Select v-model="filterLoggedBy" :options="loggedByOptions" optionLabel="label" optionValue="value" placeholder="Logged By" showClear class="filter-item" @change="applyFilters" />
              <Select v-model="filterSubjectTitle" :options="subjectTitleFilterOptions" optionLabel="label" optionValue="value" placeholder="Subject Title" showClear class="filter-item" filter @change="applyFilters" />
              <Select v-model="filterCancelled" :options="cancelledOptions" optionLabel="label" optionValue="value" placeholder="Status" showClear class="filter-item" @change="applyFilters" />
            </div>
            <div class="filter-summary">
              <span class="result-count">{{ filteredReferrals.length }} record(s)</span>
              <Button v-if="hasActiveFilters" label="Clear Filters" icon="pi pi-filter-slash" size="small" severity="secondary" text @click="clearAllFilters" />
            </div>
          </div>
        </template>
        <Column field="hearing_date_raw" header="Hearing Date" sortable style="min-width: 110px">
          <template #body="{ data }">{{ data.hearing_date || '—' }}</template>
        </Column>
        <Column field="requesting_party" header="Requesting Party" sortable style="min-width: 140px" />
        <Column field="committee_ngas_op" header="Committee" sortable style="min-width: 180px" />
        <Column header="Time & Venue" style="min-width: 150px">
          <template #body="{ data }">
            <div v-if="data.hearing_time || data.hearing_place">
              <div v-if="data.hearing_time"><i class="pi pi-clock" style="font-size: 0.75rem;"></i> {{ data.hearing_time }}</div>
              <div v-if="data.hearing_place" style="font-size: 0.85rem; color: #64748b;"><i class="pi pi-map-marker" style="font-size: 0.75rem;"></i> {{ data.hearing_place }}</div>
            </div>
            <span v-else class="text-muted">—</span>
          </template>
        </Column>
        <Column field="agenda" header="Agenda" sortable style="min-width: 200px">
          <template #body="{ data }">
            <span v-if="data.agenda" style="white-space: pre-line;">{{ data.agenda }}</span>
            <span v-else class="text-muted">—</span>
          </template>
        </Column>
        <Column field="relevant_provisions" header="Relevant Provisions" style="min-width: 180px">
          <template #body="{ data }">
            <span v-if="data.relevant_provisions" style="white-space: pre-wrap; font-size: 0.85rem;">{{ data.relevant_provisions?.substring(0, 100) }}{{ data.relevant_provisions?.length > 100 ? '...' : '' }}</span>
            <span v-else class="text-muted">—</span>
          </template>
        </Column>
        <Column field="referral" header="Referred To" style="min-width: 120px">
          <template #body="{ data }">
            <span v-if="data.referral && data.referral.length">{{ Array.isArray(data.referral) ? data.referral.join(', ') : data.referral }}</span>
            <span v-else class="text-muted">—</span>
          </template>
        </Column>
        <Column field="confirmed_attendees" header="Attendee/s" style="min-width: 140px" />
        <Column field="request_hearing_converted" header="Hearing" sortable style="min-width: 80px">
          <template #body="{ data }">
            <Tag :value="data.request_hearing_converted" :severity="data.request_hearing ? 'success' : 'secondary'" />
          </template>
        </Column>
        <Column field="request_data_converted" header="Data" sortable style="min-width: 70px">
          <template #body="{ data }">
            <Tag :value="data.request_data_converted" :severity="data.request_data ? 'success' : 'secondary'" />
          </template>
        </Column>
        <Column field="request_position_paper_converted" header="PP" sortable style="min-width: 70px">
          <template #body="{ data }">
            <Tag :value="data.request_position_paper_converted" :severity="data.request_position_paper ? 'success' : 'secondary'" />
          </template>
        </Column>
        <Column header="Actions" style="min-width: 90px" v-if="proxy.$authStore.user?.role?.includes('pllg-staff')">
          <template #body="{ data }">
            <div class="actions">
              <Button icon="pi pi-pencil" severity="info" size="small" :loading="editLoading === data.id" @click="openEditModal(data)"/>
              <Button icon="pi pi-trash" severity="danger" size="small" @click="deleteReferral(data.id)" />
            </div>
          </template>
        </Column>
      </DataTable>

      <!-- Two Weeks View -->
      <div v-if="viewMode === 'twoweeks'" class="calendar-view">
        <h3>This Week & Next Week</h3>
        <div class="week-grid">
          <div v-for="day in twoWeekDays" :key="day.date" class="day-card" :class="{ today: day.isToday }">
            <div class="day-header">
              <span class="day-name">{{ day.dayName }}</span>
              <span class="day-date">{{ day.dateFormatted }}</span>
            </div>
            <div class="day-events">
              <div v-for="item in day.items" :key="item.id" class="day-event">
                <div class="event-committee">{{ item.committee_ngas_op }}</div>
                <div class="event-time">{{ item.hearing_time }}</div>
              </div>
              <div v-if="day.items.length === 0" class="no-events">No hearings</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Month View -->
      <div v-if="viewMode === 'month'" class="calendar-view">
        <div class="month-header">
          <Button icon="pi pi-chevron-left" size="small" severity="secondary" @click="prevMonth" />
          <h3>{{ currentMonthName }}</h3>
          <Button icon="pi pi-chevron-right" size="small" severity="secondary" @click="nextMonth" />
        </div>
        <div class="month-grid">
          <div class="month-day-header" v-for="d in ['Mon','Tue','Wed','Thu','Fri']" :key="d">{{ d }}</div>
          <div v-for="day in monthDays" :key="day.date" class="month-day-cell" :class="{ empty: !day.date, today: day.isToday, 'has-events': day.items && day.items.length > 0 }">
            <div class="month-day-num" v-if="day.date">{{ day.dayNum }}</div>
            <div class="month-day-events" v-if="day.items">
              <div v-for="item in day.items.slice(0, 2)" :key="item.id" class="month-event-dot" :title="item.committee_ngas_op + ' - ' + item.agenda">
                {{ item.committee_ngas_op?.substring(0, 20) }}
              </div>
              <div v-if="day.items.length > 2" class="month-more">+{{ day.items.length - 2 }} more</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Metrics View -->
      <div v-if="viewMode === 'metrics'" class="metrics-view">
        <div class="metrics-grid">
          <div class="metric-card">
            <h4>By Congress Period</h4>
            <div class="metric-bars">
              <div v-for="item in metricsCongress" :key="item.label" class="metric-bar-row">
                <span class="metric-label">{{ item.label }}</span>
                <div class="metric-bar-container">
                  <div class="metric-bar" :style="{ width: item.percent + '%' }"></div>
                </div>
                <span class="metric-count">{{ item.count }}</span>
              </div>
            </div>
          </div>
          <div class="metric-card">
            <h4>By Requesting Party</h4>
            <div class="metric-bars">
              <div v-for="item in metricsRequestingParty" :key="item.label" class="metric-bar-row">
                <span class="metric-label">{{ item.label }}</span>
                <div class="metric-bar-container">
                  <div class="metric-bar pie-bar" :style="{ width: item.percent + '%' }"></div>
                </div>
                <span class="metric-count">{{ item.count }}</span>
              </div>
            </div>
          </div>
          <div class="metric-card">
            <h4>By Committee (Top 10)</h4>
            <div class="metric-bars">
              <div v-for="item in metricsCommittee" :key="item.label" class="metric-bar-row">
                <span class="metric-label" :title="item.label">{{ item.label?.substring(0, 30) }}{{ item.label?.length > 30 ? '...' : '' }}</span>
                <div class="metric-bar-container">
                  <div class="metric-bar stacked-bar" :style="{ width: item.percent + '%' }"></div>
                </div>
                <span class="metric-count">{{ item.count }}</span>
              </div>
            </div>
          </div>
          <div class="metric-card">
            <h4>By Logged By</h4>
            <div class="metric-bars">
              <div v-for="item in metricsLoggedBy" :key="item.label" class="metric-bar-row">
                <span class="metric-label">{{ item.label }}</span>
                <div class="metric-bar-container">
                  <div class="metric-bar logged-bar" :style="{ width: item.percent + '%' }"></div>
                </div>
                <span class="metric-count">{{ item.count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Dialog v-model:visible="showModal" :header="isEditing ? 'Edit Referral' : 'Add Referral'" :modal="true" :style="{width: '80vw'}">
      <Form v-slot="$form" :key="formKey" :initialValues :resolver @submit="saveReferral" class="flex flex-col gap-4 w-full sm:w-56">
        <div class="form-grid">
          <p class="required-note"><i class="pi pi-info-circle"></i> All fields marked with * are required</p>
          <div class="form-row">
            <div class="form-group">
              <FloatLabel variant="in">
                <Select id="congress_period" name="congress_period" :options="congressOptions" optionLabel="label" optionValue="value" v-model="initialValues.congress_period" showClear />
                <label for="congress_period">Congress Period *</label>
              </FloatLabel>
            </div>
            
            <div class="form-group">
              <FloatLabel>
                <DatePicker id="date_received" name="date_received" dateFormat="yy-mm-dd"/>
                <label for="date_received">Date Received *</label>
              </FloatLabel>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="field-label">Request for Hearing Attendance *</label>
              <SelectButton name="request_hearing" :options="yesNoOptions" optionLabel="label" optionValue="value"/>
            </div>

            <div class="form-group">
              <label class="field-label">Request for Data *</label>
              <SelectButton name="request_data" :options="yesNoOptions" optionLabel="label" optionValue="value"/>
            </div>

            <div class="form-group">
              <label class="field-label">Request for Position Paper / Comments *</label>
              <SelectButton name="request_position_paper" :options="yesNoOptions" optionLabel="label" optionValue="value"/>
            </div>
          </div>

          <div class="form-group field-with-info">
            <FloatLabel variant="in">
              <Textarea id="agenda" name="agenda" rows="3"/>
              <label for="agenda">Agenda *</label>
            </FloatLabel>
            <i class="pi pi-info-circle info-icon" v-tooltip="'Must be in title case format'"></i>
          </div>

          <div class="form-group field-with-info">
            <FloatLabel variant="in">
              <InputText id="agenda_link" name="agenda_link" v-model="initialValues.agenda_link"/>
              <label for="agenda_link">Agenda Link</label>
            </FloatLabel>
          </div>

          <div class="form-group">
            <FloatLabel variant="in">
              <Textarea id="relevant_provisions" name="relevant_provisions" v-model="initialValues.relevant_provisions" rows="3" />
              <label for="relevant_provisions">Relevant Provisions</label>
            </FloatLabel>
          </div>

          <div class="form-row">
            <div class="form-group">
              <FloatLabel variant="in">
                <Select id="communication" name="communication" :options="commsOptions" optionLabel="label" optionValue="value" showClear />
                <label for="communication">Communication *</label>
              </FloatLabel>
            </div>

            <div class="form-group">
              <FloatLabel variant="in">
                <Select id="requesting_party" name="requesting_party" :options="requestPartyOptions" optionLabel="label" optionValue="value"  v-model="initialValues.requesting_party" showClear/>
                <label for="requesting_party">Requesting Party *</label>
              </FloatLabel>
            </div>

            <div class="form-group">
              <FloatLabel variant="in">
                
                <Select v-model="initialValues.committee_ngas_op" :options="committeeOptionsGrouped" optionLabel="label" optionValue="value" optionGroupLabel="category" optionGroupChildren="items" filter class="w-full"  />

                <label for="committee_ngas_op">Committee/NGAs/OP Office *</label>
              </FloatLabel>
            </div>

          </div>

          <div v-if="$form.request_position_paper?.value" class="position-paper-section">
            <div class="form-group">
                <label class="field-label">Unnumbered Substitute Bill/Draft Bill *</label>
                <SelectButton id="unnumbered_substitute_bill" name="unnumbered_substitute_bill" :options="yesNoOptions" optionLabel="label" optionValue="value"/>
            </div>
          </div>

          <div v-if="$form.request_hearing?.value" class="hearing-section">
            <div class="form-row">
              <div class="form-group">
                <FloatLabel variant="in">
                  <DatePicker id="hearing_date" name="hearing_date" dateFormat="yy-mm-dd"/>
                  <label for="hearing_date">Hearing Date *</label>
                </FloatLabel>
              </div>

              <div class="form-group">
                <FloatLabel variant="in">
                  <DatePicker id="hearing_time" name="hearing_time" hourFormat="12" timeOnly />
                  <label for="hearing_time">Hearing Time *</label>
                </FloatLabel>
              </div>
            </div>

            <div class="form-group">
              <FloatLabel variant="in">
                <InputText id="hearing_place"name="hearing_place"/>
                <label for="hearing_place">Hearing Place *</label>
              </FloatLabel>
            </div>
          </div>

          <div class="form-group field-with-info">
            <FloatLabel variant="in">
              <Select id="notice_cancellation" name="notice_cancellation" :options="rescheduledOptions" optionLabel="label" optionValue="value" showClear  />
              <label for="notice_cancellation">Notice of Cancellation/Reschedule</label>
            </FloatLabel>
          </div>

          <div class="form-group">
            <FloatLabel variant="in">
              <MultiSelect id="referral" name="referral" :options="referralOptions" optionLabel="label" optionValue="value" optionGroupLabel="category" optionGroupChildren="items" filter showClear/>
              <label for="referral">Referral *</label>
            </FloatLabel>
          </div>

          <div class="form-group field-with-info">
            <FloatLabel variant="in">
              <Textarea id="confirmed_attendees" name="confirmed_attendees" rows="2" />
              <label for="confirmed_attendees">Confirmed Attendees</label>
            </FloatLabel>
            <i class="pi pi-info-circle info-icon" v-tooltip="'Preferably Identified DLLOs (Director Level) that confirmed their attendance'"></i>
          </div>

          <div class="form-group">
            <FloatLabel variant="in">
              <DatePicker id="deadline" name="deadline" dateFormat="yy-mm-dd" />
              <label for="deadline">Deadline for Position Papers / Comments / Data</label>
            </FloatLabel>
          </div>

          <div class="form-group field-with-info">
            <FloatLabel variant="in">
              <InputChips id="house_bill_no" name="house_bill_no" v-model="initialValues.house_bill_no" separator="," addOnBlur />
              <label for="house_bill_no">House Bill No</label>
            </FloatLabel>
            <i class="pi pi-info-circle info-icon" v-tooltip="'must only contain numbers'"></i>
          </div>

          <div class="form-group field-with-info">
            <FloatLabel variant="in">
              <InputChips id="senate_bill_no" name="senate_bill_no" v-model="initialValues.senate_bill_no" separator="," addOnBlur />
              <label for="senate_bill_no">Senate Bill No</label>
            </FloatLabel>
            <i class="pi pi-info-circle info-icon" v-tooltip="'must only contain numbers'"></i>
          </div>

          <div class="form-group field-with-info">
            <FloatLabel variant="in">
              <InputChips id="proposed_resolution_no" name="proposed_resolution_no" v-model="initialValues.proposed_resolution_no" separator="," addOnBlur />
              <label for="proposed_resolution_no">Proposed Resolution No</label>
            </FloatLabel>
            <i class="pi pi-info-circle info-icon" v-tooltip="'must only contain numbers'"></i>
           
          </div>

          <div class="form-group  field-with-info">
            <FloatLabel variant="in">
              <InputChips id="other"  name="other" v-model="initialValues.other" separator="," addOnBlur />
              <label for="other">Other</label>
            </FloatLabel>
             <i class="pi pi-info-circle info-icon" v-tooltip="'ex. Proposed Joint Resolution No. 10, Privilege Speech of Sen. Hontiveros on Online Gambling dated 9 September 2025'"></i>
          </div>

        </div>
        
        <div class="modal-actions">
          <Button type="button" label="Cancel" severity="secondary" icon="pi pi-times" @click="closeModal" />
          <Button type="submit" :label="isEditing ? 'Update' : 'Create'" icon="pi pi-check" :loading="saving" />
        </div>
      </Form>
    </Dialog>
  </div>
</template>


<script setup>

/**
 * @fileoverview PLLG Tracker - Referrals Management Component
 * 
 * This component manages the PLLG (Policy and Legislative Liaison Group) tracker system
 * for tracking legislative referrals, hearing requests, and position paper requirements.
 * 
 * @component Referrals
 * @requires vue
 * @requires @primevue/forms
 * @requires sweetalert2
 * @requires ../axios.js
 * @requires ../assets/committees-agencies.json
 * 
 * @description
 * Features:
 * - View all referrals in a paginated, searchable data table
 * - Create new referrals with comprehensive form validation
 * - Edit existing referrals
 * - Delete referrals with confirmation
 * - Role-based access control (pllg-staff only can add/edit/delete)
 * - Input sanitization for XSS protection
 * - Conditional form fields based on request types
 * 
 * @security
 * - Input sanitization removes HTML tags (<>) from text inputs
 * - Array inputs are sanitized element by element
 * - Role-based UI restrictions for CRUD operations
 * 
 * @author LMP Development Team
 * @version 1.0.0
 */
 

import { ref, computed, onMounted, getCurrentInstance, watch  } from 'vue'
import { Form } from '@primevue/forms'
import Tag from 'primevue/tag'
import Swal from 'sweetalert2'
import api from '../axios.js'


const { proxy } = getCurrentInstance()

// ============================================================================
// STATE MANAGEMENT
// ============================================================================

/**
 * List of all referrals
 */
const referrals = ref([])

/**
 * Controls modal visibility
 */
const showModal = ref(false)

/**
 * Indicates if form is in edit mode
 */
const isEditing = ref(false)

/**
 * Indicates if save operation is in progress
 */
const saving = ref(false)
const formKey = ref(0)

/**
 * Indicates if data is being loaded
 */
const loading = ref(false)
const editLoading = ref(null)

/**
 * Available referral office options
 */
const referralOptions = ref([])

/**
 * Filter global settings
 */
const filters = ref({
  global: { value: null, matchMode: 'contains' }
})

const viewMode = ref('table')
const filterCongress = ref('20')
const filterRequestingParty = ref(null)
const filterCommittee = ref(null)
const filterLoggedBy = ref(null)
const filterSubjectTitle = ref(null)
const filterCancelled = ref(null)
const currentMonth = ref(new Date())

const requestingPartyOptions = computed(() => {
  const parties = [...new Set(referrals.value.map(r => r.requesting_party).filter(Boolean))]
  return parties.sort().map(p => ({ label: p, value: p }))
})

const committeeOptions = computed(() => {
  const committees = [...new Set(referrals.value.map(r => r.committee_ngas_op).filter(Boolean))]
  return committees.sort().map(c => ({ label: c, value: c }))
})

const loggedByOptions = computed(() => {
  const loggers = [...new Set(referrals.value.map(r => r.logged_by).filter(Boolean))]
  return loggers.sort().map(l => ({ label: l, value: l }))
})

const subjectTitleFilterOptions = computed(() => {
  const titles = [...new Set(referrals.value.map(r => r.subject_title).filter(Boolean))]
  return titles.sort().map(t => ({ label: t, value: t }))
})

const cancelledOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Cancelled', value: 'CANCELLED' },
  { label: 'Postponed', value: 'POSTPONED' },
  { label: 'Rescheduled', value: 'RESCHEDULED' }
]

const filteredReferrals = computed(() => {
  return referrals.value.filter(r => {
    if (filterCongress.value && r.congress_period !== filterCongress.value) return false
    if (filterRequestingParty.value && r.requesting_party !== filterRequestingParty.value) return false
    if (filterCommittee.value && r.committee_ngas_op !== filterCommittee.value) return false
    if (filterLoggedBy.value && r.logged_by !== filterLoggedBy.value) return false
    if (filterSubjectTitle.value && r.subject_title !== filterSubjectTitle.value) return false
    if (filterCancelled.value) {
      if (filterCancelled.value === 'active') {
        if (['CANCELLED', 'POSTPONED', 'RESCHEDULED'].includes(r.notice_cancellation)) return false
      } else {
        if (r.notice_cancellation !== filterCancelled.value) return false
      }
    }
    return true
  })
})

const applyFilters = () => { /* reactive computed handles it */ }

const hasActiveFilters = computed(() => {
  return filterRequestingParty.value || filterCommittee.value || filterLoggedBy.value || filterSubjectTitle.value || filterCancelled.value
})

const clearAllFilters = () => {
  filterRequestingParty.value = null
  filterCommittee.value = null
  filterLoggedBy.value = null
  filterSubjectTitle.value = null
  filterCancelled.value = null
}

// Two Weeks View
const twoWeekDays = computed(() => {
  const days = []
  const today = new Date()
  const startOfWeek = new Date(today)
  startOfWeek.setDate(today.getDate() - today.getDay() + 1) // Monday

  for (let i = 0; i < 10; i++) { // 2 weeks x 5 weekdays
    const d = new Date(startOfWeek)
    d.setDate(startOfWeek.getDate() + Math.floor(i / 5) * 7 + (i % 5))
    const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    const isoDate = d.toISOString().split('T')[0]
    const items = filteredReferrals.value.filter(r => {
      if (!r.hearing_date) return false
      const hd = new Date(r.hearing_date)
      return hd.toISOString().split('T')[0] === isoDate
    })
    days.push({
      date: isoDate,
      dayName: d.toLocaleDateString('en-US', { weekday: 'short' }),
      dateFormatted: dateStr,
      isToday: isoDate === today.toISOString().split('T')[0],
      items
    })
  }
  return days
})

// Month View
const currentMonthName = computed(() => {
  return currentMonth.value.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
})

const monthDays = computed(() => {
  const year = currentMonth.value.getFullYear()
  const month = currentMonth.value.getMonth()
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const today = new Date().toISOString().split('T')[0]

  // Start from Monday
  let startDow = firstDay.getDay() - 1
  if (startDow < 0) startDow = 6

  const days = []
  // Empty cells before first day (skip weekends)
  for (let i = 0; i < startDow; i++) {
    days.push({ date: null })
  }

  for (let d = 1; d <= lastDay.getDate(); d++) {
    const date = new Date(year, month, d)
    const dow = date.getDay()
    if (dow === 0 || dow === 6) continue // skip weekends
    const isoDate = date.toISOString().split('T')[0]
    const items = filteredReferrals.value.filter(r => {
      if (!r.hearing_date) return false
      const hd = new Date(r.hearing_date)
      return hd.toISOString().split('T')[0] === isoDate
    })
    days.push({
      date: isoDate,
      dayNum: d,
      isToday: isoDate === today,
      items
    })
  }
  return days
})

const prevMonth = () => {
  const d = new Date(currentMonth.value)
  d.setMonth(d.getMonth() - 1)
  currentMonth.value = d
}
const nextMonth = () => {
  const d = new Date(currentMonth.value)
  d.setMonth(d.getMonth() + 1)
  currentMonth.value = d
}

// Metrics
const metricsCongress = computed(() => buildMetric(filteredReferrals.value, 'congress_period'))
const metricsRequestingParty = computed(() => buildMetric(filteredReferrals.value, 'requesting_party'))
const metricsCommittee = computed(() => buildMetric(filteredReferrals.value, 'committee_ngas_op', 10))
const metricsLoggedBy = computed(() => buildMetric(filteredReferrals.value, 'logged_by'))

const buildMetric = (data, field, limit) => {
  const counts = {}
  data.forEach(r => {
    const val = r[field] || 'Unknown'
    counts[val] = (counts[val] || 0) + 1
  })
  let sorted = Object.entries(counts).sort((a, b) => b[1] - a[1])
  if (limit) sorted = sorted.slice(0, limit)
  const max = sorted.length > 0 ? sorted[0][1] : 1
  return sorted.map(([label, count]) => ({ label, count, percent: (count / max) * 100 }))
}
// ============================================================================
// FORM OPTIONS
// ============================================================================

/**
 * Yes/No options for boolean fields
 * @constant
 */
const yesNoOptions = [
  { label: 'Yes', value: true },
  { label: 'No', value: false }
]

/**
 * Grouped committee options from JSON data
 * @constant
 */
const committeeOptionsGrouped = ref([])

const loadCommitteesAgencies = async (congressNo, requestingParty) => {
  try {
    const response = await api.get(`/committees-agencies-grouped?congress_no=${congressNo}&requesting_party=${requestingParty}`)
    committeeOptionsGrouped.value = response.data.map(group => ({
      category: group.label,
      items: group.items.map(item => ({ label: item.name, value: item.name }))
    }))
  } catch (error) {
    console.error('Error loading committees/agencies:', error)
  }
}

/**
 * Congress period options
 * @constant
 */
const congressOptions = [
  { label: '20th Congress', value: '20' },
  { label: '19th Congress', value: '19' }
]

const congressFilterOptions = [
  { label: '20th Congress', value: '20' },
  { label: '19th Congress', value: '19' }
]

/**
 * Communication options
 * @constant
 */
const commsOptions = [
  { label: 'Formal (Email/Memo/Letter)', value: 'Formal (Email/Memo/Letter)' },
  { label: 'Informal (Word of Mouth, Message, Call, Viber)', value: 'Informal (Word of Mouth, Message, Call, Viber)' },
]

/**
 * Requesting Party options
 * @constant
 */
const requestPartyOptions = [
  { label: 'House', value: 'HOUSE' },
  { label: 'Senate', value: 'SENATE' },
  { label: 'National Government Agency', value: 'NGA' },
  { label: 'DOF Attached Agency', value: 'DAA' },
  { label: 'DOF Office', value: 'DO' },
  { label: 'Others', value: 'OTHERS' }
]

/**
 * Reschedule options
 * @constant
 */
const rescheduledOptions = [
  { label: 'Rescheduled', value: 'RESCHEDULED' },
  { label: 'Postponed', value: 'POSTPONED' },
  { label: 'Cancelled', value: 'CANCELLED' }
]

/**
 * Form initial values
 * @constant
 */
const initialValues = ref({
  id: null,
  congress_period: '',
  date_received: null,
  request_hearing: false,
  request_data: false,
  request_position_paper: false,
  agenda: null,
  agenda_link: null,
  relevant_provisions: '',
  communication: '',
  requesting_party: '',
  committee_ngas_op: '',
  notice_cancellation: '',
  referral: '',
  confirmed_attendees: '',
  deadline: null,
  house_bill_no: [],
  senate_bill_no: [],
  proposed_resolution_no: [],
  other: [],
  hearing_date: null,
  hearing_time: null,
  hearing_place: '',
  unnumbered_substitute_bill: false
})


// ============================================================================
// FORM VALIDATION
// ============================================================================
/**
 * Form validation resolver
 * @param {Object} params - Validation parameters
 * @param {Object} params.values - Form values to validate
 * @returns {Object} Validation result with values and errors
 */
const resolver = ({ values }) => {
    const errors = {};
    const required = ['congress_period', 'date_received', 'agenda', 'communication','requesting_party','committee_ngas_op','referral'];
    
    // Validate required fields
    required.forEach(field => {
        if (!values[field]) errors[field] = [{ message: '' }];
    });

    // Validate boolean fields
    if (values.request_hearing === null || values.request_hearing === undefined) {
        errors.request_hearing = [{ message: '' }];
    }
    if (values.request_data === null || values.request_data === undefined) {
        errors.request_data = [{ message: '' }];
    }
    if (values.request_position_paper === null || values.request_position_paper === undefined) {
        errors.request_position_paper = [{ message: '' }];
    }

    // Conditional validation for hearing details
    if (values.request_hearing) {
        if (!values.hearing_date) errors.hearing_date = [{ message: '' }];
        if (!values.hearing_time) errors.hearing_time = [{ message: '' }];
        if (!values.hearing_place) errors.hearing_place = [{ message: '' }];
    }

    // Conditional validation for position paper
    if (values.request_position_paper) {
        if (values.unnumbered_substitute_bill === null || values.unnumbered_substitute_bill === undefined) {
            errors.unnumbered_substitute_bill = [{ message: '' }];
        }
    }

    return { values, errors };
};


// ============================================================================
// INPUT SANITIZATION
// ============================================================================
/**
 * Sanitizes string input by removing HTML tags and trimming whitespace
 * @param {string|*} input - Input to sanitize
 * @returns {string|*} Sanitized input or original if not a string
 */
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input
  return input.replace(/[<>]/g, '').trim()
}

/**
 * Sanitizes array of strings
 * @param {Array} arr - Array to sanitize
 * @returns {Array} Sanitized array
 */
const sanitizeArray = (arr) => {
  if (!Array.isArray(arr)) return []
  return arr.map(item => sanitizeInput(item))
}

// ============================================================================
// DATA LOADING
// ============================================================================

/**
 * Loads all referrals from API
 * @async
 * @returns {Promise<void>}
 */
const loadReferrals = async () => {
  try {
    loading.value = true
    const response = await api.get('/pllg-tracker')
    referrals.value = response.data
  } catch (error) {
    console.error('Error loading referrals:', error)
  } finally {
    loading.value = false
  }
}

/**
 * Loads referral office options from API
 * @async
 * @returns {Promise<void>}
 */
const loadReferralOptions = async () => {
  try {
    const response = await api.get('/agencies-grouped')
    referralOptions.value = response.data.map(group => ({
      category: group.label,
      items: group.items.map(item => ({ label: item.name, value: item.code }))
    }))
  } catch (error) {
    console.error('Error loading referral options:', error)
  }
}

// ============================================================================
// MODAL OPERATIONS
// ============================================================================

/**
 * Opens modal for adding new referral
 * Resets form to initial state
 */
const openAddModal = () => {
  isEditing.value = false
  initialValues.value = {
    id: null,
    congress_period: '',
    date_received: null,
    request_hearing: false,
    request_data: false,
    request_position_paper: false,
    logged_by: '',
    logged_datetime: new Date(),
    agenda: '',
    agenda_link: '',
    relevant_provisions: '',
    communication: '',
    requesting_party: '',
    committee_ngas_op: '',
    notice_cancellation: '',
    referral: '',
    confirmed_attendees: '',
    deadline: null,
    house_bill_no: [],
    senate_bill_no: [],
    proposed_resolution_no: [],
    other: [],
    hearing_date: null,
    hearing_time: null,
    hearing_place: '',
    unnumbered_substitute_bill: false
  }
  formKey.value++
  showModal.value = true
}

/**
 * Opens modal for editing existing referral
 * @param {Object} referral - Referral data to edit
 */
const openEditModal = async (referral) => {
  editLoading.value = referral.id
  isEditing.value = true
  const data = { ...referral }
  if (typeof data.committee_ngas_op === 'string') {
    // data.committee_ngas_op = data.committee_ngas_op ? [data.committee_ngas_op] : ''
  }
  initialValues.value = data
  initialValues.value._originalCommittee = data.committee_ngas_op
  initialValues.value._originalRequestingParty = data.requesting_party
  if (referral.congress_period && referral.requesting_party) {
    await loadCommitteesAgencies(referral.congress_period, referral.requesting_party)
  }
  formKey.value++
  editLoading.value = null
  showModal.value = true
}

/**
 * Closes modal and resets saving state
 */
const closeModal = () => {
  showModal.value = false
  saving.value = false
}

// ============================================================================
// CRUD OPERATIONS
// ============================================================================

/**
 * Saves referral (create or update)
 * @async
 * @param {Object} params - Form submission parameters
 * @param {boolean} params.valid - Form validation status
 * @param {Object} params.values - Form values
 * @returns {Promise<void>}
 */
const saveReferral = async ({ valid, values }) => {

  // Sanitize all inputs
  const payload = {
    ...values,
    id: initialValues.value.id,
    congress_period: sanitizeInput(values.congress_period),
    agenda: sanitizeInput(values.agenda),
    agenda_link: sanitizeInput(values.agenda_link),
    relevant_provisions: initialValues.value.relevant_provisions || null,
    confirmed_attendees: sanitizeInput(values.confirmed_attendees),
    hearing_place: sanitizeInput(values.hearing_place),
    committee_ngas_op: sanitizeInput(initialValues.value.committee_ngas_op),
    requesting_party: sanitizeInput(values.requesting_party),
    communication: sanitizeInput(values.communication),
    house_bill_no: sanitizeArray(initialValues.value.house_bill_no),
    senate_bill_no: sanitizeArray(initialValues.value.senate_bill_no),
    proposed_resolution_no: sanitizeArray(initialValues.value.proposed_resolution_no),
    other: sanitizeArray(initialValues.value.other)
  };

    if(valid) {
      // Determine if email will be sent
      let confirmText = `Are you sure you want to ${isEditing.value ? 'update' : 'create'} this referral?`
      if (!isEditing.value) {
        confirmText += '\n\nAn email notification will be sent to the assigned Liaison Officer/s.'
      } else if (initialValues.value._originalCommittee !== initialValues.value.committee_ngas_op || initialValues.value._originalRequestingParty !== values.requesting_party) {
        confirmText += '\n\nThe committee/requesting party has changed. An email notification will be sent to the newly assigned Liaison Officer/s.'
      }

      const result = await Swal.fire({
        title: 'Confirm Action',
        text: confirmText,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Proceed',
        cancelButtonText: 'Cancel'
      })
      
      if (!result.isConfirmed) return
      
      try {
        saving.value = true

        // API call
        if (isEditing.value) {
          await api.put(`/pllg-tracker/${payload.id}`, payload)
        } else {
          await api.post('/pllg-tracker', payload)
        }
        
        closeModal()
        loadReferrals()
        
        // Success notification
        Swal.fire({
          title: 'Success!',
          text: `Referral ${isEditing.value ? 'updated' : 'created'} successfully`,
          icon: 'success'
        })
      } catch (error) {

        // Error notification
        Swal.fire({
          title: 'Error!',
          text: error.response?.data?.message || `Failed to ${isEditing.value ? 'update' : 'create'} referral`,
          icon: 'error'
          })
        } finally {
          saving.value = false
        }

    }

}

/**
 * Deletes referral with confirmation
 * @async
 * @param {number} id - Referral ID to delete
 * @returns {Promise<void>}
 */
const deleteReferral = async (id) => {
  const result = await Swal.fire({
    title: 'Delete Referral',
    text: 'Are you sure?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    confirmButtonColor: '#ef4444'
  })
  
  if (result.isConfirmed) {
    try {
      await api.delete(`/pllg-tracker/${id}`)
      loadReferrals()
      Swal.fire('Deleted!', 'Referral deleted successfully', 'success')
    } catch (error) {
      console.error('Error deleting referral:', error)
    }
  }
}

// ============================================================================
// LIFECYCLE HOOKS
// ============================================================================

watch(() => initialValues.value.requesting_party, (newVal, oldVal) => {
  if (newVal) loadCommitteesAgencies(initialValues.value.congress_period, newVal)
  if (oldVal && oldVal !== newVal) {
    initialValues.value.committee_ngas_op = []
  }
})

/**
 * Component mounted hook
 * Loads initial data
 */
onMounted(() => {
  loadReferrals()
  loadReferralOptions()
})
</script>

<style scoped>
.page {
  padding: 1rem 0;
  background: var(--p-surface-50);
  min-height: 100vh;
}

.container {
  /* background: rgba(255, 255, 255, 0.95); */
  backdrop-filter: blur(10px);
  border-radius: 20px;
  margin: 1rem;
  padding: 1.5rem;
  /* box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1); */
  width: calc(100vw - 2rem);
  max-width: none;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #1e3a8a;
}

.page-header h1 {
  color: #1e3a8a;
  font-size: 2rem;
  font-weight: 700;
}

.add-btn {
  background: #1e3a8a;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
}

.add-btn:hover {
  background: #1e40af;
}

.meeting-tabs {
  display: flex;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e5e7eb;
}

.tab-btn {
  background: none;
  border: none;
  padding: 1rem 2rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.3s;
}

.tab-btn:hover {
  color: #1e3a8a;
}

.tab-btn.active {
  color: #1e3a8a;
  border-bottom-color: #1e3a8a;
}

.table-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
}

.table-header-multi {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.table-filters {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.5rem;
}

.search-field {
  grid-column: span 2;
}

.search-field :deep(.p-inputtext) {
  height: 100%;
}

.filter-item {
  width: 100% !important;
}

.filter-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.5rem;
  border-top: 1px solid #f1f5f9;
}

.result-count {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 500;
}

.filter-row {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.filter-select {
  min-width: 170px;
}

.view-controls {
  margin-bottom: 1rem;
}

.view-buttons {
  display: flex;
  gap: 0.25rem;
}

.text-muted {
  color: #94a3b8;
}

/* Calendar Views */
.calendar-view {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
}

.calendar-view h3 {
  color: #1e3a8a;
  margin-bottom: 1rem;
  text-align: center;
}

.week-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.5rem;
}

.day-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.5rem;
  min-height: 120px;
}

.day-card.today {
  border-color: #3b82f6;
  background: #eff6ff;
}

.day-header {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 0.5rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid #f1f5f9;
}

.day-events {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.day-event {
  font-size: 0.75rem;
  padding: 0.25rem 0.4rem;
  background: #dbeafe;
  border-radius: 4px;
  border-left: 3px solid #3b82f6;
}

.event-committee {
  font-weight: 600;
  color: #1e3a8a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-time {
  color: #64748b;
  font-size: 0.7rem;
}

.no-events {
  font-size: 0.75rem;
  color: #cbd5e1;
  text-align: center;
  padding: 1rem 0;
}

/* Month View */
.month-header {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.month-header h3 {
  margin: 0;
  min-width: 180px;
  text-align: center;
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 2px;
}

.month-day-header {
  text-align: center;
  font-weight: 700;
  font-size: 0.8rem;
  color: #1e3a8a;
  padding: 0.5rem;
  background: #f1f5f9;
}

.month-day-cell {
  border: 1px solid #f1f5f9;
  min-height: 80px;
  padding: 0.25rem;
  font-size: 0.75rem;
}

.month-day-cell.today {
  background: #eff6ff;
  border-color: #3b82f6;
}

.month-day-cell.has-events {
  background: #f0fdf4;
}

.month-day-num {
  font-weight: 700;
  color: #374151;
  font-size: 0.8rem;
  margin-bottom: 0.2rem;
}

.month-event-dot {
  font-size: 0.65rem;
  padding: 0.1rem 0.3rem;
  background: #dbeafe;
  border-radius: 3px;
  margin-bottom: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #1e40af;
}

.month-more {
  font-size: 0.65rem;
  color: #64748b;
  font-weight: 600;
}

/* Metrics */
.metrics-view {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
}

.metrics-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.metric-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
}

.metric-card h4 {
  color: #1e3a8a;
  margin: 0 0 1rem 0;
  font-size: 0.95rem;
}

.metric-bars {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.metric-bar-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.metric-label {
  font-size: 0.8rem;
  color: #374151;
  min-width: 80px;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.metric-bar-container {
  flex: 1;
  height: 20px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
}

.metric-bar {
  height: 100%;
  background: linear-gradient(90deg, #1e3a8a, #3b82f6);
  border-radius: 4px;
  transition: width 0.3s;
}

.metric-bar.pie-bar {
  background: linear-gradient(90deg, #10b981, #34d399);
}

.metric-bar.stacked-bar {
  background: linear-gradient(90deg, #f59e0b, #fbbf24);
}

.metric-bar.logged-bar {
  background: linear-gradient(90deg, #8b5cf6, #a78bfa);
}

.metric-count {
  font-size: 0.8rem;
  font-weight: 700;
  color: #1e3a8a;
  min-width: 30px;
  text-align: right;
}

.congress-filter {
  min-width: 200px;
}

.table-header h3 {
  color: #1e3a8a;
  margin: 0;
}

.chamber-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.chamber-badge.senate {
  background: #dbeafe;
  color: #2563eb;
}

.chamber-badge.house {
  background: #d1fae5;
  color: #065f46;
}

.chamber-badge.other {
  background: #fef3c7;
  color: #d97706;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.scheduled {
  background: #e0e7ff;
  color: #3730a3;
}

.status-badge.ongoing {
  background: #fef3c7;
  color: #d97706;
}

.status-badge.completed {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.cancelled {
  background: #fee2e2;
  color: #dc2626;
}

.status-badge.postponed {
  background: #f3e8ff;
  color: #7c3aed;
}

.link-btn {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.link-btn:hover {
  text-decoration: underline;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.edit-btn, .delete-btn {
  padding: 0.25rem 0.75rem;
  border: none;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.edit-btn {
  background: #3b82f6;
  color: white;
}

.edit-btn:hover {
  background: #2563eb;
}

.delete-btn {
  background: #ef4444;
  color: white;
}

.delete-btn:hover {
  background: #dc2626;
}

.modal-form {
  padding: 0;
}

.required-note {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: #eff6ff;
  border-left: 4px solid #3b82f6;
  color: #1e40af;
  font-size: 0.9rem;
}

.required-note i {
  color: #3b82f6;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.field-label {
  font-weight: 600;
  color: #828fa1;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.button-group {
  display: flex;
  gap: 0.5rem;
}

.button-group button {
  flex: 1;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.field-with-info {
  position: relative;
}

.agendas-section {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
  background: #f8fafc;
}

.agendas-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.agendas-header h4 {
  margin: 0;
  color: #1e3a8a;
  font-size: 0.95rem;
}

.agenda-row {
  margin-bottom: 0.75rem;
}

.agenda-row-fields {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.agenda-row-fields .form-group {
  flex: 1;
}

.agenda-list-item {
  font-size: 0.85rem;
  padding: 0.15rem 0;
}

.info-icon {
  position: absolute;
  top: 12px;
  right: 12px;
  color: #3b82f6;
  cursor: help;
  font-size: 1rem;
}

.form-group .p-inputtext,
.form-group .p-textarea,
.form-group .p-dropdown,
.form-group .p-datepicker,
.form-group .p-MultiSelect {
  width: 100% !important;
  box-sizing: border-box;
}

.form-group :deep(.p-select),
.form-group :deep(.p-multiselect) {
  min-height: 3.5rem;
}

.form-group .p-datepicker .p-inputtext,
.form-group .p-dropdown .p-dropdown-label,
.form-group .p-MultiSelect .p-MultiSelect-label {
  width: 100% !important;
}

/* Additional specificity for PrimeVue components */
.form-group .p-component {
  width: 100% !important;
}

.form-group .p-element {
  width: 100% !important;
}

/* Specific rules for MultiSelect component */
.form-group .p-MultiSelect {
  width: 100% !important;
  min-width: 0 !important;
}

.form-group .p-MultiSelect .p-MultiSelect-label {
  width: 100% !important;
}

.form-group .p-MultiSelect .p-MultiSelect-trigger {
  width: auto !important;
}

.form-group .p-inputchips {
  width: 100% !important;
  height: 60px !important;
 
}

.form-group :deep(.p-inputchips .p-inputchips-input-container) {
  padding-top: 2rem !important;
  min-height: 4rem !important;
}

.form-group :deep(.p-inputchips-chip) {
  padding-top: 1.2rem !important;
}

.form-group :deep(.p-floatlabel.p-variant-in:has(.p-inputchips) label) {
  top: 0.5rem !important;
  font-size: 0.75rem !important;
  transform: translateY(0) !important;
}

.form-group :deep(.p-floatlabel.p-variant-in .p-inputchips) {
  padding-top: 0 !important;
}

.hearing-section {


  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.hearing-section h3 {
  color: #1e3a8a;
  margin: 0;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

@media (max-width: 768px) {
  .committee-meetings-page {
    padding: 1rem;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .modal {
    width: 95%;
  }
}
</style>
